import type { ReactNode } from "react";
import { useLocale } from "../state/i18n";
import {
  QUESTION_KINDS,
  defaultOptions,
  joinCheckboxAnswers,
  needsOptions,
  newQuestion,
  questionOptions,
  splitCheckboxAnswers,
  type FormQuestion,
  type QuestionKind,
} from "../domain/questions";

export function QuestionCard({
  id,
  label,
  onLabel,
  labelEs,
  onLabelEs,
  typeControl,
  required,
  onRequired,
  canRemove,
  onRemove,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  locked,
  preview,
  extra,
}: {
  id: string;
  label: string;
  onLabel: (value: string) => void;
  labelEs?: string;
  onLabelEs?: (value: string) => void;
  typeControl: ReactNode;
  required: boolean;
  onRequired?: (value: boolean) => void;
  canRemove?: boolean;
  onRemove?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  locked: boolean;
  preview: ReactNode;
  extra?: ReactNode;
}) {
  return (
    <div className="wire design-q">
      <div className="design-q-top">
        <div className="field">
          <label htmlFor={id}>Question</label>
          <input
            id={id}
            type="text"
            value={label}
            onChange={(e) => onLabel(e.target.value)}
            disabled={locked}
          />
        </div>
        {typeControl}
      </div>
      {onLabelEs ? (
        <div className="field" style={{ marginTop: "0.75rem" }}>
          <label htmlFor={`${id}-es`}>Question (Spanish)</label>
          <input
            id={`${id}-es`}
            type="text"
            value={labelEs ?? ""}
            onChange={(e) => onLabelEs(e.target.value)}
            disabled={locked}
            placeholder="Optional. Families see this in Español."
          />
        </div>
      ) : null}
      {extra}
      <div className="design-preview">{preview}</div>
      <div className="design-q-tools">
        <label className="checks" style={{ margin: 0 }}>
          <input
            type="checkbox"
            checked={required}
            onChange={(e) => onRequired?.(e.target.checked)}
            disabled={locked || !onRequired}
          />
          Required
        </label>
        {onMoveUp || onMoveDown || canRemove ? (
          <div className="design-q-moves">
            {onMoveUp ? (
              <button
                type="button"
                className="ghost"
                disabled={locked || !canMoveUp}
                onClick={onMoveUp}
              >
                Up
              </button>
            ) : null}
            {onMoveDown ? (
              <button
                type="button"
                className="ghost"
                disabled={locked || !canMoveDown}
                onClick={onMoveDown}
              >
                Down
              </button>
            ) : null}
            {canRemove ? (
              <button
                type="button"
                className="ghost"
                disabled={locked}
                onClick={onRemove}
              >
                Remove
              </button>
            ) : (
              <span className="anno" style={{ margin: 0 }}>
                Stays on every year
              </span>
            )}
          </div>
        ) : (
          <span className="anno" style={{ margin: 0 }}>
            Stays on every year
          </span>
        )}
      </div>
    </div>
  );
}

export function QuestionFill({
  question,
  value,
  onChange,
  disabled,
}: {
  question: FormQuestion;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const { locale, t } = useLocale();
  const englishOptions = question.options.filter((item) => item.trim());
  const labels = questionOptions(question, locale);
  if (question.kind === "paragraph") {
    return (
      <textarea
        id={question.id}
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={question.required}
        disabled={disabled}
      />
    );
  }
  if (question.kind === "yesno") {
    return (
      <div className="checks">
        {["yes", "no"].map((choice) => (
          <label key={choice}>
            <input
              type="radio"
              name={question.id}
              value={choice}
              checked={value === choice}
              onChange={() => onChange(choice)}
              required={question.required}
              disabled={disabled}
            />
            {choice === "yes" ? t("fill.yes") : t("fill.no")}
          </label>
        ))}
      </div>
    );
  }
  if (question.kind === "radio") {
    return (
      <div className="checks">
        {englishOptions.map((option, index) => (
          <label key={option}>
            <input
              type="radio"
              name={question.id}
              value={option}
              checked={value === option}
              onChange={() => onChange(option)}
              required={question.required}
              disabled={disabled}
            />
            {labels[index] ?? option}
          </label>
        ))}
      </div>
    );
  }
  if (question.kind === "checkboxes") {
    const selected = splitCheckboxAnswers(value);
    return (
      <div className="checks">
        {englishOptions.map((option, index) => (
          <label key={option}>
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={(e) => {
                const next = e.target.checked
                  ? [...selected, option]
                  : selected.filter((item) => item !== option);
                onChange(joinCheckboxAnswers(next));
              }}
              disabled={disabled}
            />
            {labels[index] ?? option}
          </label>
        ))}
      </div>
    );
  }
  if (question.kind === "dropdown") {
    return (
      <select
        id={question.id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={question.required}
        disabled={disabled}
      >
        <option value="">{t("fill.select")}</option>
        {englishOptions.map((option, index) => (
          <option key={option} value={option}>
            {labels[index] ?? option}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      id={question.id}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={question.required}
      disabled={disabled}
    />
  );
}

function QuestionPreview({ question }: { question: FormQuestion }) {
  return (
    <QuestionFill
      question={question}
      value=""
      onChange={() => undefined}
      disabled
    />
  );
}

function OptionEditor({
  question,
  locked,
  onChange,
}: {
  question: FormQuestion;
  locked: boolean;
  onChange: (options: string[], optionsEs: string[]) => void;
}) {
  if (!needsOptions(question.kind)) return null;
  const optionsEs = question.options.map(
    (_, index) => question.optionsEs[index] ?? "",
  );
  return (
    <div className="design-options">
      <div className="meta">Choices</div>
      {question.options.map((option, index) => (
        <div className="design-option design-option--bilingual" key={`${question.id}-opt-${index}`}>
          <input
            type="text"
            value={option}
            aria-label={`Choice ${index + 1} English`}
            placeholder="English"
            onChange={(e) => {
              const options = question.options.map((row, rowIndex) =>
                rowIndex === index ? e.target.value : row,
              );
              onChange(options, optionsEs);
            }}
            disabled={locked}
          />
          <input
            type="text"
            value={optionsEs[index] ?? ""}
            aria-label={`Choice ${index + 1} Spanish`}
            placeholder="Spanish (optional)"
            onChange={(e) => {
              const nextEs = optionsEs.map((row, rowIndex) =>
                rowIndex === index ? e.target.value : row,
              );
              onChange(question.options, nextEs);
            }}
            disabled={locked}
          />
          <button
            type="button"
            className="ghost"
            disabled={locked || question.options.length <= 2}
            onClick={() =>
              onChange(
                question.options.filter((_, rowIndex) => rowIndex !== index),
                optionsEs.filter((_, rowIndex) => rowIndex !== index),
              )
            }
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type="button"
        className="ghost"
        disabled={locked}
        onClick={() =>
          onChange(
            [...question.options, `Option ${question.options.length + 1}`],
            [...optionsEs, ""],
          )
        }
      >
        Add choice
      </button>
    </div>
  );
}

export function ExtraQuestionsEditor({
  questions,
  onChange,
  locked = false,
  emptyLabel = "No extra questions",
}: {
  questions: FormQuestion[];
  onChange: (next: FormQuestion[]) => void;
  locked?: boolean;
  emptyLabel?: string;
}) {
  function setAt(index: number, patch: Partial<FormQuestion>) {
    onChange(
      questions.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        const next = { ...row, ...patch };
        if (patch.kind && patch.kind !== row.kind) {
          next.options = needsOptions(patch.kind)
            ? row.options.length >= 2
              ? row.options
              : defaultOptions(patch.kind)
            : [];
          next.optionsEs = needsOptions(patch.kind)
            ? next.options.map((_, optionIndex) => row.optionsEs[optionIndex] ?? "")
            : [];
        }
        return next;
      }),
    );
  }

  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= questions.length) return;
    const next = [...questions];
    const [row] = next.splice(index, 1);
    next.splice(target, 0, row);
    onChange(next);
  }

  function addQuestion() {
    onChange([...questions, newQuestion()]);
  }

  return (
    <>
      {questions.length === 0 ? (
        <div className="wire dashed design-empty">
          <div className="meta">{emptyLabel}</div>
          <p>
            Add short text, a paragraph, yes/no, multiple choice, checkboxes,
            or a dropdown.
          </p>
          <button type="button" disabled={locked} onClick={addQuestion}>
            Add question
          </button>
        </div>
      ) : (
        questions.map((item, index) => (
          <QuestionCard
            key={item.id}
            id={`extra-label-${item.id}`}
            label={item.label}
            onLabel={(value) => setAt(index, { label: value })}
            labelEs={item.labelEs}
            onLabelEs={(value) => setAt(index, { labelEs: value })}
            typeControl={
              <div className="field">
                <label htmlFor={`extra-kind-${item.id}`}>Answer type</label>
                <select
                  id={`extra-kind-${item.id}`}
                  value={item.kind}
                  onChange={(e) =>
                    setAt(index, { kind: e.target.value as QuestionKind })
                  }
                  disabled={locked}
                >
                  {QUESTION_KINDS.map((kind) => (
                    <option key={kind.value} value={kind.value}>
                      {kind.label}
                    </option>
                  ))}
                </select>
              </div>
            }
            extra={
              <OptionEditor
                question={item}
                locked={locked}
                onChange={(options, optionsEs) =>
                  setAt(index, { options, optionsEs })
                }
              />
            }
            required={item.required}
            onRequired={(value) => setAt(index, { required: value })}
            canRemove
            onRemove={() =>
              onChange(questions.filter((row) => row.id !== item.id))
            }
            canMoveUp={index > 0}
            canMoveDown={index < questions.length - 1}
            onMoveUp={() => move(index, -1)}
            onMoveDown={() => move(index, 1)}
            locked={locked}
            preview={<QuestionPreview question={item} />}
          />
        ))
      )}
      {questions.length > 0 ? (
        <div className="actions" style={{ marginTop: "0.75rem" }}>
          <button
            type="button"
            className="ghost"
            disabled={locked}
            onClick={addQuestion}
          >
            Add question
          </button>
        </div>
      ) : null}
    </>
  );
}
